/**
 * RBAC-3.5 · The permission editor's model: what each area of the role editor renders, and what a
 * control change does to the role's permission strings (FR-9, FR-9a, FR-9b, FR-10, FR-11, FR-12,
 * UX-DR3, UX-DR5–8).
 *
 * Built on two modules and restating neither:
 *  - `permissionControls.ts` (RBAC-2.1) says which strings form a noun's capability and scope
 *    ladders, and what a control position grants (`grantsFor`) or reads as (`readControls`);
 *  - `permissionDisplay.ts` (RBAC-2.2) says what renders, under which area, with what copy.
 *
 * What a noun becomes on screen:
 *  - a scope control that the database enforces (or will, once its reader lands) → a **ladder** with
 *    a capability control AND a scope control (UX-DR5);
 *  - no scope, or a `dormant` one, and two or more rungs → a ladder with NO scope control. Absent,
 *    not disabled (FR-9a): Projects is here, because nothing narrows projects by a `.team` string;
 *  - no scope and one rung → a single **switch** (FR-11), writing every string of that rung.
 * Every remaining rendered row is a switch, a **destructive** switch outside any ladder (FR-10), or a
 * **locked** reserved row (FR-12).
 *
 * ⚠️ Editing here produces a DRAFT. Saving is RBAC-3.6's `role_save_permissions()`, behind a diff
 * naming who it affects (`ReviewChangesDialog.tsx`); the seam is the plain `string[]` this module emits.
 * Strings the editor does not render (hidden, reader-pending) are carried through every edit
 * untouched, so a draft never silently drops what it cannot show.
 *
 * Pure, no DOM, no Supabase.
 */
import { grantsFor, NOUNS, PERMISSION_READINGS, readControls, type NounControls } from './permissionControls';
import { PERMISSION_AREAS, PERMISSION_DISPLAY, rendersInEditor, type PermissionArea } from './permissionDisplay';
import type { Scope } from './fieldPermissions';

export interface RungOption {
  /** `null` is None. */
  value: string | null;
  label: string;
}

export interface ScopeOption {
  value: Scope;
  label: string;
}

export interface LadderItem {
  kind: 'ladder';
  noun: string;
  title: string;
  description: string;
  rungs: RungOption[];
  capability: string | null;
  /** Null when the noun has no enforced scope. The control is then absent, never disabled. */
  scope: { options: ScopeOption[]; value: Scope } | null;
  /** UX-DR6: what the chosen combination means in practice. */
  consequence: string;
  /** "None", "Edit", "Edit, their team". */
  state: string;
}

export interface SwitchItem {
  kind: 'switch';
  keys: string[];
  label: string;
  description: string;
  on: boolean;
}

export interface DestructiveItem {
  kind: 'destructive';
  keys: string[];
  label: string;
  description: string;
  /** UX-DR7: what using it cannot take back. */
  irreversible: string;
  on: boolean;
}

export interface LockedItem {
  kind: 'locked';
  keys: string[];
  label: string;
  /** Says what it reaches and ends "Only Joules can grant it." */
  description: string;
  held: boolean;
}

export type EditorItem = LadderItem | SwitchItem | DestructiveItem | LockedItem;

export interface EditorArea {
  area: PermissionArea;
  /** UX-DR3: the collapsed header's own state. */
  summary: string;
  items: EditorItem[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Copy
// ─────────────────────────────────────────────────────────────────────────────

const RUNG_LABEL: Record<string, string> = { view: 'View', edit: 'Edit', manage: 'Manage', review: 'Review' };

const SCOPE_LABEL: Record<Scope, string> = { own: 'Own only', team: 'Their team', all: 'Everyone' };
const SCOPE_STATE: Record<Scope, string> = { own: 'own only', team: 'their team', all: 'everyone' };

interface NounCopy {
  title: string;
  description: string;
}

/** Ladder headings. A noun that renders without one here is a test failure, not a blank heading. */
const NOUN_COPY: Record<string, NounCopy> = {
  project: { title: 'Projects', description: 'Redline pages, project tasks, and changing project records.' },
  pipeline: { title: 'Pipeline boards', description: 'Moving projects between stages, and changing the stages themselves.' },
  finance: { title: 'Project finance', description: "A project's Finance section and its commission set-up." },
  contracts: { title: 'Contracts', description: 'Contracts, lenders and change orders.' },
  teams: { title: 'Team managers', description: 'Who manages each team.' },
  lead: { title: 'Leads', description: 'Seeing and working leads, and whose leads that reaches.' },
  territory: { title: 'Areas and territories', description: 'Areas and territories on the map, and whose that reaches.' },
  door: { title: 'Doors', description: 'Doors on the map. Do Not Knock doors are always shown.' },
  'knock.review': { title: 'Flagged knocks', description: 'Confirming or restoring knocks flagged by verification, and answering review requests.' },
  'field.live_view': { title: 'Live rep map', description: 'Where reps are during a knocking session. Every view is recorded and the rep is told.' },
};

/**
 * Unscoped ladders: one sentence per rung, written from the RBAC-2.2 descriptions (which follow the
 * call-site audit). Each states the whole of what the rung allows, not a delta from the one below.
 */
const FLAT_CONSEQUENCE: Record<string, Record<string, string>> = {
  project: {
    none: 'Cannot open the Redlines or Tasks pages, or change a project. Which projects they see is not decided here.',
    view: 'Can open the Redlines and Tasks pages and see redline pricing. Which projects they see is not decided here.',
    edit: "Can open the Redlines and Tasks pages, and edit a project's overview, notes, contributors and commission shares. Which projects they reach is not decided here.",
    manage: "Can edit projects, move them between stages, and open a project's Contracts, Lender, Partner, Sales Team and Reports sections. Which projects they reach is not decided here.",
  },
  pipeline: {
    none: "Cannot move projects between stages on the residential board, or change a pipeline's stages.",
    edit: 'Can drag projects between stages on the residential board. Cannot change the stages themselves.',
    manage: "Can drag projects between stages on the residential board, add and change a pipeline's stages, and filter boards by team and owner.",
  },
  finance: {
    none: "Cannot see a project's Finance section or commission set-up.",
    view: "Can see a project's Finance section, commission plans and shares. Cannot change them.",
    edit: 'Can see and change project finance, commission plans, override rules and contributor shares.',
  },
  contracts: {
    none: 'Cannot see contracts.',
    view: "Can see a project's Contracts tab and Lender section. Cannot issue change orders.",
    manage: 'Can see contracts, issue change orders and set who must sign them.',
  },
  teams: {
    none: 'Cannot change who manages a team.',
    edit: 'Can set or change who manages a team.',
    manage: 'Can set or change who manages a team, exactly as Edit allows.',
  },
};

interface ScopedCopy {
  none: string;
  verb: Record<string, string>;
  object: string;
  tail?: string;
}

/** Scoped ladders: a verb per rung and an object, put into one frame per scope. */
const SCOPED_CONSEQUENCE: Record<string, ScopedCopy> = {
  lead: { none: 'Cannot see leads.', verb: { view: 'see', edit: 'see and work', manage: 'see, work, reassign and merge' }, object: 'leads' },
  territory: { none: 'Cannot see areas or territories.', verb: { view: 'see', manage: 'see, draw, split and assign' }, object: 'areas and territories' },
  door: { none: 'Cannot see doors on the map.', verb: { view: 'see' }, object: 'doors' },
  'knock.review': { none: 'Cannot review flagged knocks.', verb: { review: 'confirm or restore' }, object: 'flagged knocks' },
  'field.live_view': { none: 'Cannot see where reps are.', verb: { view: 'see' }, object: 'reps', tail: ' live' },
};

/**
 * UX-DR7's paragraph, per destructive permission. ⚠️ Each says only what is TRUE (NFR-13): a removed
 * person can be restored, by an Admin (`restore-deleted-user`), so "cannot be undone" would be false
 * for `user.manage_all` as a blanket claim.
 */
export const DESTRUCTIVE_PARAGRAPH: Readonly<Record<string, string>> = {
  'user.manage_all':
    'Removing someone from the account takes their access away at once, and this permission cannot undo it: only an Admin can restore a removed person.',
  'dnk.remove':
    'Once a door is off Do Not Knock, reps can be sent to it. A knock made before it is marked again cannot be taken back.',
};

// ─────────────────────────────────────────────────────────────────────────────
// Nouns on screen
// ─────────────────────────────────────────────────────────────────────────────

const hasScopeControl = (n: NounControls) => !!n.scope && n.scope.status !== 'dormant';

const nounStrings = (n: NounControls): string[] =>
  [...new Set([...n.capability.flatMap((c) => c.strings), ...(n.scope?.team ?? []), ...(n.scope?.all ?? [])])];

/** Nouns with at least one rendered string, in declaration order. */
const RENDERED_NOUNS: NounControls[] = Object.values(NOUNS).filter((n) => nounStrings(n).some(rendersInEditor));

const nounArea = (n: NounControls): PermissionArea => {
  const k = nounStrings(n).find((s) => PERMISSION_DISPLAY[s]);
  if (!k) throw new Error(`permissionEditor: ${n.noun} has no displayed string`);
  return PERMISSION_DISPLAY[k].area;
};

const isLadder = (n: NounControls) => hasScopeControl(n) || n.capability.length > 1;

/** A one-rung, unscoped noun's strings: what its switch writes. */
const nounSwitchKeys = (n: NounControls) => grantsFor(n.noun, n.capability[0].rung, null);

const scopeOptions = (n: NounControls): ScopeOption[] =>
  (n.scope!.floor === 'own' ? (['own', 'team', 'all'] as Scope[]) : (['team', 'all'] as Scope[]))
    .map((value) => ({ value, label: SCOPE_LABEL[value] }));

const defaultScope = (n: NounControls): Scope => (n.scope!.floor === 'own' ? 'own' : 'team');

function rungLabel(rung: string): string {
  const l = RUNG_LABEL[rung];
  if (!l) throw new Error(`permissionEditor: no label for rung "${rung}"`);
  return l;
}

/** UX-DR6. Throws for a noun or rung with no copy, so a new ladder fails a test, not a screen. */
export function ladderConsequence(nounName: string, capability: string | null, scope: Scope | null): string {
  const flat = FLAT_CONSEQUENCE[nounName];
  if (flat) {
    const line = flat[capability ?? 'none'];
    if (!line) throw new Error(`permissionEditor: no consequence for ${nounName} at ${capability}`);
    return line;
  }
  const c = SCOPED_CONSEQUENCE[nounName];
  if (!c) throw new Error(`permissionEditor: no consequence copy for ${nounName}`);
  if (capability === null) return c.none;
  const verb = c.verb[capability];
  if (!verb || !scope) throw new Error(`permissionEditor: no consequence for ${nounName} at ${capability}/${scope}`);
  const tail = c.tail ?? '';
  const floorNone = NOUNS[nounName].scope?.floor === 'none';
  switch (scope) {
    case 'own':
      return `Can ${verb} their own ${c.object}${tail} only. Cannot reach anyone else's.`;
    case 'team':
      return floorNone
        ? `Can ${verb} their team's ${c.object}${tail}. Cannot reach another team's.`
        : `Can ${verb} their own ${c.object} and their team's${tail}. Cannot reach another team's.`;
    case 'all':
      return `Can ${verb} all ${c.object} in the account${tail}.`;
  }
}

/**
 * RBAC-3.7: one rung of a ladder described on its own, for the by-capability lookup. Unscoped, it is
 * the rung's consequence line; scoped, it says what the rung allows and that each role sets the reach.
 */
export function rungSummary(nounName: string, rung: string): string {
  if (FLAT_CONSEQUENCE[nounName]) return ladderConsequence(nounName, rung, null);
  const c = SCOPED_CONSEQUENCE[nounName];
  const verb = c?.verb[rung];
  if (!verb) throw new Error(`permissionEditor: no summary for ${nounName} at ${rung}`);
  return `Can ${verb} ${c.object}${c.tail ?? ''}. Each role sets whose that reaches.`;
}

function ladderItem(n: NounControls, held: readonly string[], remembered: Scope | undefined): LadderItem {
  const copy = NOUN_COPY[n.noun];
  if (!copy) throw new Error(`permissionEditor: no heading for ${n.noun}`);
  const pos = readControls(n.noun, held);
  const scoped = hasScopeControl(n);
  const options = scoped ? scopeOptions(n) : [];
  const legal = (s: Scope | null | undefined): s is Scope => !!s && options.some((o) => o.value === s);
  const scopeValue: Scope | null = !scoped
    ? null
    : pos.capability !== null && legal(pos.scope) ? pos.scope : legal(remembered) ? remembered : defaultScope(n);
  const state = pos.capability === null
    ? 'None'
    : `${rungLabel(pos.capability)}${scopeValue ? `, ${SCOPE_STATE[scopeValue]}` : ''}`;
  return {
    kind: 'ladder',
    noun: n.noun,
    title: copy.title,
    description: copy.description,
    rungs: [{ value: null, label: 'None' }, ...n.capability.map((c) => ({ value: c.rung, label: rungLabel(c.rung) }))],
    capability: pos.capability,
    scope: scoped ? { options, value: scopeValue! } : null,
    consequence: ladderConsequence(n.noun, pos.capability, scopeValue),
    state,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The editor
// ─────────────────────────────────────────────────────────────────────────────

const KIND_ORDER: Record<EditorItem['kind'], number> = { ladder: 0, switch: 1, destructive: 2, locked: 3 };

const granted = (i: EditorItem) =>
  i.kind === 'ladder' ? i.capability !== null : i.kind === 'locked' ? i.held : i.on;

function areaSummary(items: readonly EditorItem[]): string {
  const counted = items.filter((i) => i.kind !== 'locked');
  const locked = items.length - counted.length;
  if (counted.length === 1 && counted[0].kind === 'ladder' && locked === 0) return counted[0].state;
  const parts: string[] = [];
  if (counted.length) parts.push(`${counted.filter(granted).length} of ${counted.length}`);
  if (locked) parts.push(`${locked} locked`);
  return parts.join(' · ');
}

/**
 * Every area the editor renders, in `PERMISSION_AREAS` order, for a role holding `held`.
 * `scopeChoice` remembers a scope picked while the capability is None (FR-9b: the controls are
 * independent, so choosing None must not throw the scope away).
 */
export function buildEditor(held: readonly string[], scopeChoice: Readonly<Record<string, Scope>> = {}): EditorArea[] {
  const h = new Set(held);
  const byArea = new Map<PermissionArea, EditorItem[]>();
  const push = (area: PermissionArea, item: EditorItem) => {
    if (!byArea.has(area)) byArea.set(area, []);
    byArea.get(area)!.push(item);
  };

  const claimed = new Set<string>();
  for (const n of RENDERED_NOUNS) {
    nounStrings(n).forEach((s) => claimed.add(s));
    const area = nounArea(n);
    if (isLadder(n)) {
      push(area, ladderItem(n, held, scopeChoice[n.noun]));
    } else {
      const keys = nounSwitchKeys(n);
      const row = PERMISSION_DISPLAY[keys.find((k) => rendersInEditor(k))!];
      push(area, { kind: 'switch', keys, label: row.label, description: row.description, on: keys.some((k) => h.has(k)) });
    }
  }

  for (const d of Object.values(PERMISSION_DISPLAY)) {
    if (claimed.has(d.key) || !rendersInEditor(d.key)) continue;
    const kind = PERMISSION_READINGS[d.key]?.kind;
    if (d.status === 'locked' || kind === 'reserved') {
      push(d.area, { kind: 'locked', keys: [d.key], label: d.label, description: d.description, held: h.has(d.key) });
    } else if (kind === 'destructive') {
      const irreversible = DESTRUCTIVE_PARAGRAPH[d.key];
      if (!irreversible) throw new Error(`permissionEditor: destructive ${d.key} has no paragraph`);
      push(d.area, { kind: 'destructive', keys: [d.key], label: d.label, description: d.description, irreversible, on: h.has(d.key) });
    } else {
      push(d.area, { kind: 'switch', keys: [d.key], label: d.label, description: d.description, on: h.has(d.key) });
    }
  }

  return PERMISSION_AREAS.filter((a) => byArea.has(a)).map((area) => {
    const items = byArea.get(area)!.sort((x, y) => KIND_ORDER[x.kind] - KIND_ORDER[y.kind]);
    return { area, items, summary: areaSummary(items) };
  });
}

const sorted = (s: Iterable<string>) => [...new Set(s)].sort();

/** The role's strings after setting a ladder. Every other string, rendered or not, is kept. */
export function applyLadder(held: readonly string[], nounName: string, capability: string | null, scope: Scope | null): string[] {
  const n = NOUNS[nounName];
  if (!n) throw new Error(`permissionEditor: unknown noun "${nounName}"`);
  const mine = new Set(nounStrings(n));
  const kept = held.filter((s) => !mine.has(s));
  return sorted([...kept, ...grantsFor(nounName, capability, hasScopeControl(n) ? scope : null)]);
}

/** The role's strings after turning a switch on or off. */
export function applySwitch(held: readonly string[], keys: readonly string[], on: boolean): string[] {
  const k = new Set(keys);
  return sorted(on ? [...held, ...keys] : held.filter((s) => !k.has(s)));
}

const signature = (i: EditorItem): string => {
  switch (i.kind) {
    case 'ladder':
      return `${i.noun}=${i.capability}${i.capability !== null && i.scope ? `/${i.scope.value}` : ''}`;
    case 'locked':
      return `${i.keys[0]}=${i.held}`;
    default:
      return `${i.keys.join('+')}=${i.on}`;
  }
};

/** How many controls differ between two sets of strings. A partly-held rung read unchanged is not one. */
export function changedItemCount(before: readonly string[], after: readonly string[]): number {
  const a = buildEditor(before).flatMap((x) => x.items).map(signature);
  const b = new Set(buildEditor(after).flatMap((x) => x.items).map(signature));
  return a.filter((s) => !b.has(s)).length;
}
